import { createAction } from '@reduxjs/toolkit'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { modifyMaterialBasedOnConflicts } from './insertEstimatedOptionOverlay'
import { drawAndLabelPaths } from './markupDocument'
import { createModification } from '../../../api/projects-api'
import { IMaterialModificationConflictAPI } from '../../../models/masterSetPlan'
import { isNonAreaJoistLine } from '../../../utils/project/project-helper-functions'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Cutout, Label, RegionTool, Select, Workspace } from '../../lib/toolBoxes/2D'
import addSelectFunctionalityToDrawable from '../../lib/utils/functionality-bindings/addSelectFunctionalityToDrawable'
import {
    getCurrentDrawableLocationsFromRootState,
    getOpacities,
    selectCurrentOverlayChunkIds,
    setEstimatedOptionOverlayChunkId,
    setOverlayChunkId,
} from '../../slices/2D'
import {
    resetMaterialModificationConflicts,
    selectMaterialModificationConflicts,
    updateConflictResolutionFlag,
    updateReviewingConflictsFlag,
    updateSavedMaterialModification,
} from '../../slices/masterSetPlan'
import { selectProjectId } from '../../slices/project'
import { IMUP2DDrawableLocation, REGION_ENUMS } from '../../types'

export const createModificationAction = createAction('createModification')

export function* handleModificationCreation() {
    const manager: PaperManager | null = yield call(managers.get2DManager)
    const projectId: number | null = yield select(selectProjectId)
    const overlayChunkIds: number[] = yield select(selectCurrentOverlayChunkIds)
    const drawableLocations: IMUP2DDrawableLocation[] = yield select(getCurrentDrawableLocationsFromRootState)
    const { lineOpacity, areaOpacity }: { lineOpacity: number; areaOpacity: number } = yield select(getOpacities)

    if (!manager || !projectId || !overlayChunkIds.length) return

    const [selectTool, workspaceTool, regionTool, cutoutTool, labelTool]: [
        Select,
        Workspace,
        RegionTool,
        Cutout,
        Label
    ] = yield call(manager.getTools, [Select.NAME, Workspace.NAME, RegionTool.NAME, Cutout.NAME, Label.NAME])

    yield call(selectTool.exitSelectMode)

    const { materialModificationConflictsToSave }: ReturnType<typeof selectMaterialModificationConflicts> =
        yield select(selectMaterialModificationConflicts)

    if (materialModificationConflictsToSave?.length) {
        const mods: IMaterialModificationConflictAPI[] = yield call(
            createModification,
            materialModificationConflictsToSave,
            projectId
        )

        yield all(
            mods.map((mod) => {
                return put(
                    updateSavedMaterialModification({
                        id: mod.id,
                        material_location_id: mod.material_location_id,
                        coordinates: mod.coordinates,
                        floor_plan_id: mod.floor_plan_id,
                        option_ids: mod.option_ids.split('_').map((opId) => parseInt(opId)),
                        cutouts: mod.cutouts,
                        shapeColor: mod.shapeColor,
                    })
                )
            })
        )
    }

    yield put(resetMaterialModificationConflicts())

    yield put(updateConflictResolutionFlag(true))
    yield put(updateReviewingConflictsFlag(false))

    const regionPaths = workspaceTool.getItemsWithCriteria(
        'data',
        (data) => data.shapeType === REGION_ENUMS.TYPE
    ) as paper.Path[]

    // Each active overlay needs this check independently -- draw whichever of them (if any)
    // don't already have their materials on the canvas.
    for (const overlayChunkId of overlayChunkIds) {
        const overlayItems = workspaceTool.getItemsWithCriteria(
            'data',
            (data) => data.document_chunk_id === overlayChunkId
        )

        if (overlayItems.length === 0) {
            yield call(
                drawAndLabelPaths,
                overlayChunkId,
                { lineOpacity, areaOpacity },
                drawableLocations,
                regionPaths,
                workspaceTool,
                regionTool
            )
        }
    }

    const visibilityTest = (item: paper.Item): boolean => !isNonAreaJoistLine(item)

    const allPaperDrawablesOnPage: paper.Item[] = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        function (data: any) {
            return data?.drawable_id || data?.aiSuggestion?.id
        }
    )

    yield all(
        allPaperDrawablesOnPage.map((item) =>
            call(
                (item: paper.Item, visTest: (item: paper.Item) => boolean) => {
                    item.visible = visTest(item)
                },
                item,
                visibilityTest
            )
        )
    )

    allPaperDrawablesOnPage.forEach((item) => {
        if (overlayChunkIds.includes(item.data.document_chunk_id)) {
            item.data.isMultiSelectable = true
            item.data.selectable = true
        } else {
            item.data.selectable = false
            item.data.isMultiSelectable = false
        }
    })

    // Safe to call once per overlay -- it filters savedMaterialModifications by option_ids.
    for (const overlayChunkId of overlayChunkIds) {
        yield call(modifyMaterialBasedOnConflicts, workspaceTool, labelTool, overlayChunkId)
    }

    yield call([cutoutTool, 'setItemMetadataUpdateFunction'], addSelectFunctionalityToDrawable)

    yield put(setOverlayChunkId(null))
    yield put(
        setEstimatedOptionOverlayChunkId({
            opt: overlayChunkIds[overlayChunkIds.length - 1],
            additionalOpts: overlayChunkIds.slice(0, -1),
            base: null,
        })
    )
}

export function* watchForModificationCreation() {
    yield takeLatest(createModificationAction.type, handleModificationCreation)
}
